import { useCallback, useState } from 'react';

import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Edit2,
  Globe,
  Star,
  Trash2,
  Wrench,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { TOOL_LABELS, TOOL_VALUES } from '@repo/shared-types';

import type { AgentNode, TransferTarget } from '@repo/shared-types';
import type { Edge, Node } from '@xyflow/react';

export interface CustomToolOption {
  id: string;
  name: string;
}

const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;

const AGENT_BUILTIN_TOOLS = [
  'transfer_to_human',
  'google_maps_search',
  'end_call',
  'send_finance_link',
] as const;

interface AgentConfigPanelProps {
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  onNodeDataChange: (nodeId: string, data: AgentNode) => void;
  onEdgeDataChange: (edgeId: string, data: { description: string }) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  onSetEntry: (nodeId: string) => void;
  transferTargets: TransferTarget[];
  agents: AgentNode[];
  customTools?: CustomToolOption[];
}

const textareaClass =
  'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-16 w-full rounded-md border px-3 py-2 text-xs focus-visible:ring-2 focus-visible:outline-none';

const AdvancedSettingsSection = ({
  data,
  update,
}: {
  data: AgentNode;
  update: (patch: Partial<AgentNode>) => void;
}) => {
  const [open, setOpen] = useState(false);
  const adv = data.advanced ?? {};

  const updateAdv = (patch: Record<string, unknown>) => update({ advanced: { ...adv, ...patch } });

  return (
    <div className="rounded-md border">
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium"
        onClick={() => setOpen(!open)}
      >
        Advanced Settings
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="space-y-3 border-t px-3 py-3">
          {/* Google Search Grounding */}
          <div className="space-y-1">
            <label className="flex items-center gap-2">
              <button
                type="button"
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                  adv.grounding_enabled
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-muted-foreground/30',
                )}
                onClick={() => updateAdv({ grounding_enabled: !adv.grounding_enabled })}
              >
                {adv.grounding_enabled && <Check size={10} />}
              </button>
              <span className="text-xs font-medium">
                <Globe size={12} className="mr-1 inline" />
                Google Search Grounding
              </span>
            </label>
            <p className="text-muted-foreground text-[10px]">
              Allow this agent to search the web for real-time information.
            </p>
          </div>

          {/* Voice */}
          <div className="space-y-1">
            <label className="text-muted-foreground text-[10px] font-medium">Voice Name</label>
            <Select
              value={data.voice_name ?? ''}
              onValueChange={(val) => update({ voice_name: val || undefined })}
            >
              <SelectTrigger className="text-xs">
                <SelectValue placeholder="Select a voice..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Puck">Puck</SelectItem>
                <SelectItem value="Charon">Charon</SelectItem>
                <SelectItem value="Kore">Kore</SelectItem>
                <SelectItem value="Fenrir">Fenrir</SelectItem>
                <SelectItem value="Aoede">Aoede</SelectItem>
                <SelectItem value="Leda">Leda</SelectItem>
                <SelectItem value="Orus">Orus</SelectItem>
                <SelectItem value="Zephyr">Zephyr</SelectItem>
                <SelectItem value="Achernar">Achernar</SelectItem>
                <SelectItem value="Autonoe">Autonoe</SelectItem>
                <SelectItem value="Callirrhoe">Callirrhoe</SelectItem>
                <SelectItem value="Despina">Despina</SelectItem>
                <SelectItem value="Erinome">Erinome</SelectItem>
                <SelectItem value="Gacrux">Gacrux</SelectItem>
                <SelectItem value="Isonoe">Isonoe</SelectItem>
                <SelectItem value="Keid">Keid</SelectItem>
                <SelectItem value="Lysithea">Lysithea</SelectItem>
                <SelectItem value="Enceladus">Enceladus</SelectItem>
                <SelectItem value="Sulafat">Sulafat</SelectItem>
                <SelectItem value="Umbriel">Umbriel</SelectItem>
                <SelectItem value="Vindemiatrix">Vindemiatrix</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-[10px]">
              Voice applies to the configuration level. Gemini Live locks voice at session start.
            </p>
          </div>

          {/* Temperature */}
          <div className="space-y-1">
            <label className="text-muted-foreground text-[10px] font-medium">Temperature</label>
            <Input
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={adv.temperature ?? ''}
              onChange={(e) =>
                updateAdv({ temperature: e.target.value ? parseFloat(e.target.value) : undefined })
              }
              placeholder="0.7"
              className="text-xs"
            />
          </div>

          {/* Language Code */}
          <div className="space-y-1">
            <label className="text-muted-foreground text-[10px] font-medium">Language Code</label>
            <Input
              value={(adv.language_code as string) ?? ''}
              onChange={(e) => updateAdv({ language_code: e.target.value || undefined })}
              placeholder="en-GB"
              className="text-xs"
            />
          </div>

          {/* Thinking Level */}
          <div className="space-y-1">
            <label className="text-muted-foreground text-[10px] font-medium">Thinking Level</label>
            <Select
              value={(adv.thinking_level as string) ?? ''}
              onValueChange={(val) => updateAdv({ thinking_level: val || undefined })}
            >
              <SelectTrigger className="text-xs">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minimal">Minimal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Model Override */}
          <div className="space-y-1">
            <label className="text-muted-foreground text-[10px] font-medium">Model Override</label>
            <Input
              value={(adv.model as string) ?? ''}
              onChange={(e) => updateAdv({ model: e.target.value || undefined })}
              placeholder="gemini-2.5-flash"
              className="text-xs"
            />
            <p className="text-muted-foreground text-[10px]">
              Only applies to text sessions. Voice sessions share one model per call.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const AiAgentForm = ({
  node,
  data,
  onChange,
  onDelete,
  onSetEntry,
  agents,
  customTools = [],
}: {
  node: Node;
  data: AgentNode;
  onChange: (data: AgentNode) => void;
  onDelete: () => void;
  onSetEntry: () => void;
  agents: AgentNode[];
  customTools?: CustomToolOption[];
}) => {
  const nameValid = data.name === '' || SNAKE_CASE_RE.test(data.name);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);

  const update = useCallback(
    (patch: Partial<AgentNode>) => onChange({ ...data, ...patch }),
    [data, onChange],
  );

  const otherAgents = agents.filter((a) => a.id !== node.id && a.type === 'ai_agent');

  const selectedCustomToolIds = data.custom_tool_ids ?? [];

  const handleCopyFrom = useCallback(
    (sourceId: string) => {
      const source = agents.find((a) => a.id === sourceId);
      if (!source) return;
      onChange({
        ...data,
        prompt_template: source.prompt_template,
        tools: source.tools ? [...source.tools] : [],
        custom_tool_ids: source.custom_tool_ids ? [...source.custom_tool_ids] : [],
        voice_name: source.voice_name,
        advanced: source.advanced ? { ...source.advanced } : undefined,
        handoff_conditions: source.handoff_conditions,
      });
    },
    [agents, data, onChange],
  );

  const promptPreview = (() => {
    const text = data.prompt_template ?? '';
    const lines = text.split('\n');
    if (lines.length <= 3) return text;
    return lines.slice(0, 3).join('\n') + '\n...';
  })();

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-sm font-semibold">AI Agent</h3>

      {/* Name */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Name</label>
        <Input
          value={data.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="billing_agent"
          className={cn('font-mono text-xs', !nameValid && 'border-destructive')}
        />
        {!nameValid && (
          <p className="text-destructive text-[10px]">Must be snake_case (e.g., billing_agent)</p>
        )}
      </div>

      {/* Role */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Role</label>
        <Input
          value={data.role}
          onChange={(e) => update({ role: e.target.value })}
          placeholder="Handles billing inquiries"
          className="text-xs"
        />
      </div>

      {/* Prompt Template */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Prompt Template</label>
        <div
          className="bg-muted/50 min-h-16 cursor-pointer rounded-md border px-3 py-2 text-xs whitespace-pre-wrap"
          onClick={() => setPromptDialogOpen(true)}
        >
          {promptPreview || <span className="text-muted-foreground">You are an agent that...</span>}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setPromptDialogOpen(true)}
        >
          <Edit2 size={12} className="mr-1.5" />
          Edit Prompt
        </Button>
        <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Edit Prompt — {data.name || 'Unnamed Agent'}</DialogTitle>
            </DialogHeader>
            <textarea
              className={cn(textareaClass, 'min-h-[400px] text-sm')}
              value={data.prompt_template ?? ''}
              onChange={(e) => update({ prompt_template: e.target.value })}
              placeholder="You are an agent that..."
            />
            <DialogFooter>
              <Button type="button" size="sm" onClick={() => setPromptDialogOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tools — Multi-select dropdown */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Tools</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-between text-xs font-normal"
            >
              <span className="flex items-center gap-1.5 truncate">
                <Wrench size={12} className="text-muted-foreground shrink-0" />
                {(() => {
                  const builtInCount = (data.tools ?? []).filter((t) =>
                    AGENT_BUILTIN_TOOLS.includes(t as (typeof AGENT_BUILTIN_TOOLS)[number]),
                  ).length;
                  const total = builtInCount + selectedCustomToolIds.length;
                  return total > 0
                    ? `${total} tool${total === 1 ? '' : 's'} selected`
                    : 'Select tools...';
                })()}
              </span>
              <ChevronDown size={14} className="text-muted-foreground shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[320px] p-1" align="start">
            <div className="max-h-[240px] overflow-y-auto">
              {/* Built-in tools */}
              <p className="text-muted-foreground px-2 py-1 text-[10px] font-semibold uppercase">
                Built-in
              </p>
              {TOOL_VALUES.filter((t) =>
                AGENT_BUILTIN_TOOLS.includes(t as (typeof AGENT_BUILTIN_TOOLS)[number]),
              ).map((tool) => {
                const checked = data.tools?.includes(tool) ?? false;
                return (
                  <button
                    key={tool}
                    type="button"
                    className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      const current = data.tools ?? [];
                      const next = checked ? current.filter((t) => t !== tool) : [...current, tool];
                      update({ tools: next });
                    }}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        checked
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground/30',
                      )}
                    >
                      {checked && <Check size={10} />}
                    </div>
                    {TOOL_LABELS[tool]}
                  </button>
                );
              })}

              {/* Custom tools */}
              {customTools.length > 0 && (
                <>
                  <div className="my-1 border-t" />
                  <p className="text-muted-foreground px-2 py-1 text-[10px] font-semibold uppercase">
                    Custom
                  </p>
                  {customTools.map((tool) => {
                    const checked = selectedCustomToolIds.includes(tool.id);
                    return (
                      <button
                        key={tool.id}
                        type="button"
                        className="hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          const next = checked
                            ? selectedCustomToolIds.filter((id) => id !== tool.id)
                            : [...selectedCustomToolIds, tool.id];
                          update({ custom_tool_ids: next });
                        }}
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                            checked
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-muted-foreground/30',
                          )}
                        >
                          {checked && <Check size={10} />}
                        </div>
                        {tool.name}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Handoff Conditions */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Handoff Conditions</label>
        <textarea
          className={textareaClass}
          value={data.handoff_conditions ?? ''}
          onChange={(e) => update({ handoff_conditions: e.target.value || undefined })}
          placeholder="Transfer when the customer asks for billing help..."
          rows={3}
        />
      </div>

      {/* Advanced Settings — Collapsible */}
      <AdvancedSettingsSection data={data} update={update} />

      {/* Set as Entry */}
      {!data.is_entry && (
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={onSetEntry}>
          <Star size={14} className="mr-1.5" />
          Set as Entry Agent
        </Button>
      )}

      {/* Copy Settings From */}
      {otherAgents.length > 0 && (
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Copy Settings From</label>
          <Select onValueChange={handleCopyFrom}>
            <SelectTrigger className="text-xs">
              <SelectValue placeholder="Select an agent..." />
            </SelectTrigger>
            <SelectContent>
              {otherAgents.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <Copy size={12} className="mr-1.5 inline" />
                  {a.name || 'Unnamed'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Delete */}
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="w-full"
        disabled={data.is_entry}
        onClick={onDelete}
      >
        <Trash2 size={14} className="mr-1.5" />
        Delete Agent
      </Button>
      {data.is_entry && (
        <p className="text-muted-foreground text-[10px]">
          Cannot delete the entry agent. Set another agent as entry first.
        </p>
      )}
    </div>
  );
};

const HumanNodeForm = ({
  data,
  onChange,
  onDelete,
  transferTargets,
}: {
  data: AgentNode;
  onChange: (data: AgentNode) => void;
  onDelete: () => void;
  transferTargets: TransferTarget[];
}) => {
  const update = useCallback(
    (patch: Partial<AgentNode>) => onChange({ ...data, ...patch }),
    [data, onChange],
  );

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-sm font-semibold">Human Transfer</h3>

      {/* Name */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Name</label>
        <Input
          value={data.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="sales_transfer"
          className="text-xs"
        />
      </div>

      {/* Role */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Role</label>
        <Input
          value={data.role}
          onChange={(e) => update({ role: e.target.value })}
          placeholder="Sales department"
          className="text-xs"
        />
      </div>

      {/* Transfer Target */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Transfer Target</label>
        <Select
          value={data.transfer_target_key ?? ''}
          onValueChange={(val) => update({ transfer_target_key: val })}
        >
          <SelectTrigger className="text-xs">
            <SelectValue placeholder="Select a transfer target..." />
          </SelectTrigger>
          <SelectContent>
            {transferTargets.map((t) => (
              <SelectItem key={t.category_key} value={t.category_key}>
                {t.category_key} — {t.category_description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Delete */}
      <Button type="button" variant="destructive" size="sm" className="w-full" onClick={onDelete}>
        <Trash2 size={14} className="mr-1.5" />
        Delete
      </Button>
    </div>
  );
};

const EdgeForm = ({
  edge,
  agents,
  onDataChange,
  onDelete,
}: {
  edge: Edge;
  agents: AgentNode[];
  onDataChange: (data: { description: string }) => void;
  onDelete: () => void;
}) => {
  const sourceName = agents.find((a) => a.id === edge.source)?.name || 'Unknown';
  const targetName = agents.find((a) => a.id === edge.target)?.name || 'Unknown';
  const description = (edge.data as { description?: string } | undefined)?.description ?? '';

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-sm font-semibold">Edge</h3>

      {/* Source -> Target */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Connection</label>
        <p className="bg-muted rounded-md px-3 py-2 font-mono text-xs">
          {sourceName} &rarr; {targetName}
        </p>
      </div>

      {/* Description */}
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Description</label>
        <textarea
          className={textareaClass}
          value={description}
          onChange={(e) => onDataChange({ description: e.target.value })}
          placeholder="Transfer when the customer asks about billing..."
          rows={4}
        />
        <p className="text-muted-foreground text-[10px]">
          Describes the condition under which the source agent hands off to the target.
        </p>
      </div>

      {/* Delete */}
      <Button type="button" variant="destructive" size="sm" className="w-full" onClick={onDelete}>
        <Trash2 size={14} className="mr-1.5" />
        Delete Edge
      </Button>
    </div>
  );
};

export const AgentConfigPanel = ({
  selectedNode,
  selectedEdge,
  onNodeDataChange,
  onEdgeDataChange,
  onDeleteNode,
  onDeleteEdge,
  onSetEntry,
  transferTargets,
  agents,
  customTools,
}: AgentConfigPanelProps) => {
  if (selectedEdge) {
    return (
      <EdgeForm
        edge={selectedEdge}
        agents={agents}
        onDataChange={(data) => onEdgeDataChange(selectedEdge.id, data)}
        onDelete={() => onDeleteEdge(selectedEdge.id)}
      />
    );
  }

  if (!selectedNode) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6">
        <AlertTriangle size={24} className="text-muted-foreground" />
        <p className="text-muted-foreground text-center text-sm">
          Select a node or edge to configure it.
        </p>
      </div>
    );
  }

  const data = selectedNode.data as AgentNode;

  if (data.type === 'human') {
    return (
      <HumanNodeForm
        data={data}
        onChange={(d) => onNodeDataChange(selectedNode.id, d)}
        onDelete={() => onDeleteNode(selectedNode.id)}
        transferTargets={transferTargets}
      />
    );
  }

  return (
    <AiAgentForm
      node={selectedNode}
      data={data}
      onChange={(d) => onNodeDataChange(selectedNode.id, d)}
      onDelete={() => onDeleteNode(selectedNode.id)}
      onSetEntry={() => onSetEntry(selectedNode.id)}
      agents={agents}
      customTools={customTools}
    />
  );
};
